import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Calendar,
  Clock,
  User as UserIcon,
  FileText,
  MessageSquare,
  Edit,
  Trash2,
  Receipt,
  ExternalLink,
  X,
  Loader2,
  ClipboardList,
  ChevronDown,
  Ticket,
} from 'lucide-react';
import { useAppointment, useCancelAppointment, useSetVisitStatus, type VisitStatus } from '@/hooks/useAppointments';
import { usePatient } from '@/hooks/usePatients';
import { useServicesList } from '@/hooks/useServices';
import { useCreateInvoiceDraft } from '@/hooks/useInvoices';
import { usePatientActivePunchCards, usePayWithPunchCard } from '@/hooks/usePunchCardPayment';
import { supabase } from '@/lib/supabase';
import { JournalModal } from './JournalModal';
import { MessageModal } from './MessageModal';

export interface AppointmentDetailsModalProps {
  appointmentId: string;
  clinicId: string;
  onClose: () => void;
  onEdit?: () => void;
}

export function AppointmentDetailsModal({
  appointmentId,
  clinicId,
  onClose,
  onEdit,
}: AppointmentDetailsModalProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const { data: appointment, isLoading } = useAppointment(appointmentId);
  const { data: patient } = usePatient(appointment?.patient_id ?? null);
  const { data: services } = useServicesList(clinicId);
  const cancelAppointment = useCancelAppointment();
  const setVisitStatus = useSetVisitStatus();
  const createInvoiceDraft = useCreateInvoiceDraft();

  const [showJournalModal, setShowJournalModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showInvoicePanel, setShowInvoicePanel] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [showPunchCardPanel, setShowPunchCardPanel] = useState(false);
  const [selectedPunchCardId, setSelectedPunchCardId] = useState<string>('');

  const { data: activePunchCards } = usePatientActivePunchCards(
    appointment?.patient_id ?? null,
    clinicId
  );
  const payWithPunchCard = usePayWithPunchCard();

  const hasPatient = Boolean(appointment?.patient_id);
  const patientName = patient
    ? `${patient.first_name} ${patient.last_name}`.trim()
    : '—';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const preVisitAssignmentId = (appointment as any)?.pre_visit_assignment_id as string | null | undefined;

  const { data: preVisitStatus } = useQuery({
    queryKey: ['pre_visit_assignment', preVisitAssignmentId],
    enabled: Boolean(preVisitAssignmentId),
    staleTime: 30_000,
    queryFn: async (): Promise<string | null> => {
      if (!preVisitAssignmentId) return null;
      const { data } = await supabase
        .from('patient_forms_assignments')
        .select('status')
        .eq('forms_assignment_id', preVisitAssignmentId)
        .maybeSingle();
      return (data as { status: string } | null)?.status ?? null;
    },
  });

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (isLoading || !appointment) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50">
        <div className="flex min-h-full items-center justify-center p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl dark:bg-gray-800">
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Format dates
  const startDate = new Date(appointment.starts_at);
  const shortDateFormatter = new Intl.DateTimeFormat(
    i18n.language === 'da' ? 'da-DK' : 'en-US',
    {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }
  );
  const timeFormatter = new Intl.DateTimeFormat(
    i18n.language === 'da' ? 'da-DK' : 'en-US',
    {
      hour: '2-digit',
      minute: '2-digit',
    }
  );

  const getStatusBadge = () => {
    const statusColors = {
      upcoming: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      no_show: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };

    const statusLabels = {
      upcoming: 'Kommende',
      completed: 'Gennemført',
      cancelled: 'Aflyst',
      no_show: 'Ikke mødt op',
    };

    const status = appointment.status as keyof typeof statusColors;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[status] || statusColors.upcoming}`}>
        {statusLabels[status] || appointment.status}
      </span>
    );
  };

  const handleDelete = async () => {
    if (!confirm(t('appointments.modal.confirm_delete', 'Er du sikker på at du vil slette denne aftale?'))) {
      return;
    }

    try {
      setIsDeleting(true);
      await cancelAppointment.mutateAsync({
        appointment_id: appointmentId,
        reason: 'Deleted by user',
      });
      toast.success(t('appointments.toasts.deleted', 'Aftale slettet'));
      onClose();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error(t('appointments.errors.unknown', 'Der opstod en fejl'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGoToPatient = () => {
    if (!hasPatient) return;
    navigate(`/patients/${appointment.patient_id}`);
    onClose();
  };

  const handleOpenInvoicePanel = () => {
    // Pre-select the appointment's service, or blank if none
    setSelectedServiceId(appointment.service_id ?? '');
    setShowInvoicePanel(true);
  };

  const handleCreateInvoice = async () => {
    if (!hasPatient) {
      toast.error(t('appointments.invoice.no_patient', 'Kan ikke oprette faktura uden patient'));
      return;
    }
    try {
      const invoice = await createInvoiceDraft.mutateAsync({
        clinic_id: appointment.clinic_id,
        patient_id: appointment.patient_id!, // Safe because hasPatient is true
        appointment_id: appointment.id,
      });
      // If user selected a different service than the appointment's own service,
      // navigate to the invoice and let them add lines there — the appointment
      // line (from appointment.service_id) was already auto-added by the RPC.
      onClose();
      navigate(`/invoices/${invoice.id}`);
    } catch (err) {
      toast.error(t('appointments.invoice.create_failed', 'Kunne ikke oprette faktura'));
      console.error('Invoice creation failed:', err);
    }
  };

  const handlePayWithPunchCard = async () => {
    if (!selectedPunchCardId || !appointment.patient_id) return;
    
    try {
      await payWithPunchCard.mutateAsync({
        punch_card_id: selectedPunchCardId,
        appointment_id: appointmentId,
        patient_id: appointment.patient_id,
        clinic_id: clinicId,
        clips_to_use: 1,
      });
      toast.success(t('appointments.payment.punch_card_payment_success', 'Betalt med klippekort'));
      setShowPunchCardPanel(false);
      setSelectedPunchCardId('');
    } catch (err) {
      toast.error(t('appointments.payment.punch_card_payment_failed', 'Kunne ikke betale med klippekort'));
      console.error('Punch card payment failed:', err);
    }
  };

  const isAlreadyPaid = appointment.payment_status === 'paid';

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        {/* Modal Container */}
        <div className="flex min-h-full items-center justify-center p-0 md:p-4">
          <div
            className="relative w-full max-w-4xl bg-white dark:bg-gray-800 md:rounded-2xl shadow-2xl min-h-screen md:min-h-0 max-h-screen md:max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Sticky on mobile */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 md:rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">
                  Aftale Detaljer
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content - Scrollable */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {/* Patient Header */}
              <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-100 dark:border-indigo-800">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {patientName}
                    </h3>
                    <div className="flex items-center gap-3 flex-wrap">
                      {patient?.email && (
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {patient.email}
                        </span>
                      )}
                      {patient?.phone && (
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {patient.phone}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>{getStatusBadge()}</div>
                </div>
              </div>

              {/* Appointment Details Section */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                    Aftale Detaljer
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Date & Time */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Dato & Tid
                    </label>
                    <div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">{shortDateFormatter.format(startDate)}</div>
                      <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                        {timeFormatter.format(startDate)}
                      </div>
                    </div>
                  </div>

                  {/* Service */}
                  {appointment.service_name && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        Behandling
                      </label>
                      <div className="text-sm text-gray-900 dark:text-white font-medium">
                        {appointment.service_name}
                      </div>
                    </div>
                  )}

                  {/* Duration */}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      Varighed
                    </label>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900 dark:text-white">
                        {Math.round((new Date(appointment.ends_at).getTime() - new Date(appointment.starts_at).getTime()) / 60000)} minutter
                      </span>
                    </div>
                  </div>

                  {/* Clinician */}
                  {appointment.clinician_name && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        Behandler
                      </label>
                      <div className="text-sm text-gray-900 dark:text-white">
                        {appointment.clinician_name}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes Section */}
              {appointment.notes && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <MessageSquare className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      Noter
                    </h4>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {appointment.notes}
                  </div>
                </div>
              )}

              {/* Pre-visit form badge (FORMS-10) */}
              {preVisitAssignmentId && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <ClipboardList className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      Pre-visit form
                    </h4>
                  </div>
                  {(() => {
                    if (!preVisitStatus || preVisitStatus === 'sent' || preVisitStatus === 'reminded') {
                      return (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                          Afventer
                        </span>
                      );
                    }
                    if (preVisitStatus === 'completed') {
                      return (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Udfyldt
                        </span>
                      );
                    }
                    return (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                        Udløbet
                      </span>
                    );
                  })()}
                </div>
              )}

              {/* Inline Invoice Creation Panel */}
              {showInvoicePanel && hasPatient && (
                <div className="mb-6 rounded-xl border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-800 dark:bg-indigo-950/30">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Receipt className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                      <h4 className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">
                        {t('appointments.invoice.panel_title', 'Opret faktura')}
                      </h4>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowInvoicePanel(false)}
                      className="text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Service / Treatment selector */}
                  <div className="mb-3">
                    <label className="mb-1 block text-xs font-medium text-indigo-800 dark:text-indigo-300">
                      {t('appointments.invoice.treatment_label', 'Behandlingstype')}
                    </label>
                    <select
                      value={selectedServiceId}
                      onChange={(e) => setSelectedServiceId(e.target.value)}
                      className="block w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-indigo-700 dark:bg-gray-800 dark:text-white"
                    >
                      <option value="">{t('appointments.invoice.no_service', '— Ingen behandlingstype —')}</option>
                      {(services ?? []).map((s) => {
                        const price = s.price_cents > 0
                          ? ` — ${(s.price_cents / 100).toLocaleString(i18n.language === 'da' ? 'da-DK' : 'en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${s.currency ?? 'DKK'}`
                          : '';
                        return (
                          <option key={s.id} value={s.id}>
                            {s.name}{price}
                          </option>
                        );
                      })}
                    </select>
                    {selectedServiceId && selectedServiceId !== (appointment.service_id ?? '') && (
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                        {t('appointments.invoice.service_mismatch_hint', 'Du kan tilføje yderligere linjer på fakturaen bagefter.')}
                      </p>
                    )}
                    {!appointment.service_id && !selectedServiceId && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('appointments.invoice.no_service_hint', 'Fakturaen oprettes uden behandlingslinje. Du kan tilføje linjer bagefter.')}
                      </p>
                    )}
                  </div>

                  {/* Price preview */}
                  {selectedServiceId && (() => {
                    const svc = (services ?? []).find(s => s.id === selectedServiceId);
                    if (!svc || svc.price_cents === 0) return null;
                    const total = svc.price_cents / 100;
                    const vat = total * (svc.vat_rate_percent / 100);
                    const excl = total - vat;
                    return (
                      <div className="mb-3 rounded-lg border border-indigo-100 bg-white px-3 py-2 text-xs dark:border-indigo-800 dark:bg-gray-800">
                        <div className="flex justify-between text-gray-600 dark:text-gray-400">
                          <span>{t('appointments.invoice.excl_vat', 'Ekskl. moms')}</span>
                          <span>{excl.toLocaleString(i18n.language === 'da' ? 'da-DK' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {svc.currency ?? 'DKK'}</span>
                        </div>
                        <div className="flex justify-between text-gray-600 dark:text-gray-400">
                          <span>{t('appointments.invoice.vat', 'Moms')} ({svc.vat_rate_percent}%)</span>
                          <span>{vat.toLocaleString(i18n.language === 'da' ? 'da-DK' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {svc.currency ?? 'DKK'}</span>
                        </div>
                        <div className="mt-1 flex justify-between border-t border-indigo-100 pt-1 font-semibold text-gray-900 dark:border-indigo-800 dark:text-white">
                          <span>{t('appointments.invoice.total', 'Total inkl. moms')}</span>
                          <span>{total.toLocaleString(i18n.language === 'da' ? 'da-DK' : 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {svc.currency ?? 'DKK'}</span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={createInvoiceDraft.isPending}
                      onClick={() => void handleCreateInvoice()}
                      className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-indigo-700 disabled:opacity-60"
                    >
                      {createInvoiceDraft.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Receipt className="h-4 w-4" />
                      )}
                      {createInvoiceDraft.isPending
                        ? t('appointments.invoice.creating', 'Opretter...')
                        : t('appointments.invoice.create_confirm', 'Opret faktura')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowInvoicePanel(false)}
                      className="rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-indigo-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}

              {/* Punch Card Payment Panel */}
              {showPunchCardPanel && hasPatient && !isAlreadyPaid && (
                <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/30">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Ticket className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                      <h4 className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
                        {t('appointments.payment.pay_with_punch_card', 'Betal med klippekort')}
                      </h4>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowPunchCardPanel(false)}
                      className="text-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  {activePunchCards && activePunchCards.length > 0 ? (
                    <>
                      <div className="mb-3">
                        <label className="mb-1 block text-xs font-medium text-emerald-800 dark:text-emerald-300">
                          {t('appointments.payment.select_punch_card', 'Vælg klippekort')}
                        </label>
                        <select
                          value={selectedPunchCardId}
                          onChange={(e) => setSelectedPunchCardId(e.target.value)}
                          className="block w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:border-emerald-700 dark:bg-gray-800 dark:text-white"
                        >
                          <option value="">{t('appointments.payment.select_punch_card', 'Vælg klippekort')}</option>
                          {activePunchCards.map((card) => (
                            <option key={card.id} value={card.id}>
                              {card.name} — {card.remaining_clips} {t('appointments.payment.clips_remaining', 'klip tilbage')}
                            </option>
                          ))}
                        </select>
                      </div>

                      {selectedPunchCardId && (() => {
                        const selectedCard = activePunchCards.find(c => c.id === selectedPunchCardId);
                        if (!selectedCard) return null;
                        return (
                          <div className="mb-3 rounded-lg border border-emerald-100 bg-white px-3 py-2 text-xs dark:border-emerald-800 dark:bg-gray-800">
                            <div className="flex justify-between text-gray-600 dark:text-gray-400">
                              <span>{t('appointments.payment.remaining_before', 'Klip før')}</span>
                              <span>{selectedCard.remaining_clips}</span>
                            </div>
                            <div className="flex justify-between text-gray-600 dark:text-gray-400">
                              <span>{t('appointments.payment.to_use', 'Brug')}</span>
                              <span>-1</span>
                            </div>
                            <div className="mt-1 flex justify-between border-t border-emerald-100 pt-1 font-semibold text-gray-900 dark:border-emerald-800 dark:text-white">
                              <span>{t('appointments.payment.remaining_after', 'Klip efter')}</span>
                              <span>{selectedCard.remaining_clips - 1}</span>
                            </div>
                          </div>
                        );
                      })()}

                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={!selectedPunchCardId || payWithPunchCard.isPending}
                          onClick={() => void handlePayWithPunchCard()}
                          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
                        >
                          {payWithPunchCard.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Ticket className="h-4 w-4" />
                          )}
                          {payWithPunchCard.isPending
                            ? t('common.processing', 'Behandler...')
                            : t('appointments.payment.use_clip', 'Brug 1 klip')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowPunchCardPanel(false)}
                          className="rounded-lg border border-emerald-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-emerald-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">
                      {t('appointments.payment.no_active_punch_cards', 'Ingen aktive klippekort')}
                    </p>
                  )}
                </div>
              )}

              {/* Visit Status Progression — only shown for upcoming appointments */}
              {appointment.status === 'upcoming' && (() => {
                const vs = appointment.visit_status as VisitStatus | null;
                const steps: { key: VisitStatus; labelKey: string; color: string; activeColor: string }[] = [
                  { key: 'checked_in',         labelKey: 'appointments.visit_status.checked_in',        color: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400', activeColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
                  { key: 'under_treatment',     labelKey: 'appointments.visit_status.under_treatment',   color: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400', activeColor: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' },
                  { key: 'treatment_finished',  labelKey: 'appointments.visit_status.treatment_finished',color: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400', activeColor: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
                  { key: 'payment_completed',   labelKey: 'appointments.visit_status.payment_completed', color: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400', activeColor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' },
                ];
                const order = steps.map(s => s.key);
                const currentIdx = vs ? order.indexOf(vs) : -1;

                const handleSetStatus = async (status: VisitStatus) => {
                  try {
                    await setVisitStatus.mutateAsync({ appointment_id: appointmentId, visit_status: status });
                    toast.success(t('appointments.visit_status.status_updated'));
                  } catch {
                    toast.error(t('appointments.visit_status.status_update_failed'));
                  }
                };

                return (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                        {t('appointments.visit_status.checked_in', 'Patient status')}
                      </h4>
                    </div>
                    <div className="flex items-center gap-1">
                      {steps.map((step, idx) => {
                        const isActive = currentIdx >= idx;
                        const isCurrent = currentIdx === idx;
                        return (
                          <button
                            key={step.key}
                            type="button"
                            disabled={setVisitStatus.isPending}
                            onClick={() => void handleSetStatus(step.key)}
                            title={t(step.labelKey)}
                            className={`flex-1 rounded-lg px-2 py-2 text-xs font-medium transition-all border-2 ${
                              isCurrent
                                ? `${step.activeColor} border-current`
                                : isActive
                                ? `${step.activeColor} border-transparent opacity-70`
                                : `${step.color} border-transparent hover:opacity-80`
                            }`}
                          >
                            <span className="block text-center leading-tight">{t(step.labelKey)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Quick Actions */}
              {hasPatient && (
                <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleGoToPatient}
                    className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
                  >
                    <UserIcon className="w-4 h-4" />
                    Se patient detaljer
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Footer - Sticky on mobile */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4 md:rounded-b-2xl">
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Primary Actions */}
                <div className="flex-1 flex flex-wrap gap-2">
                  {hasPatient && (
                    <>
                      <button
                        onClick={() => setShowJournalModal(true)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <FileText className="w-4 h-4" />
                        <span className="hidden sm:inline">Journal</span>
                      </button>

                      <button
                        onClick={() => setShowMessageModal(true)}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span className="hidden sm:inline">Besked</span>
                      </button>

                      <button
                        onClick={handleOpenInvoicePanel}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                          showInvoicePanel
                            ? 'bg-indigo-50 text-indigo-700 border-indigo-300 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700'
                            : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        <Receipt className="w-4 h-4" />
                        <span className="hidden sm:inline">{t('appointments.invoice.button', 'Opret Faktura')}</span>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showInvoicePanel ? 'rotate-180' : ''}`} />
                      </button>

                      {!isAlreadyPaid && (
                        <button
                          onClick={() => setShowPunchCardPanel(!showPunchCardPanel)}
                          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                            showPunchCardPanel
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
                              : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                        >
                          <Ticket className="w-4 h-4" />
                          <span className="hidden sm:inline">{t('appointments.payment.punch_card', 'Klippekort')}</span>
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showPunchCardPanel ? 'rotate-180' : ''}`} />
                        </button>
                      )}
                    </>
                  )}

                  {onEdit && appointment.status === 'upcoming' && (
                    <button
                      onClick={() => {
                        onClose();
                        onEdit();
                      }}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      <span className="hidden sm:inline">Rediger</span>
                    </button>
                  )}
                  {onEdit && appointment.status !== 'upcoming' && (
                    <div className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg cursor-not-allowed" title="Kun kommende aftaler kan redigeres">
                      <Edit className="w-4 h-4" />
                      <span className="hidden sm:inline">Rediger</span>
                    </div>
                  )}
                </div>

                {/* Delete Button */}
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>{isDeleting ? 'Sletter...' : 'Slet'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Journal Modal */}
      {showJournalModal && hasPatient && (
        <JournalModal
          patientId={appointment.patient_id!}
          clinicId={clinicId}
          appointmentId={appointment.id}
          appointmentDate={startDate}
          appointmentVisitStatus={appointment.visit_status}
          onClose={() => setShowJournalModal(false)}
          onCreated={() => {
            setShowJournalModal(false);
            toast.success('Journal oprettet');
          }}
        />
      )}

      {/* Message Modal */}
      {showMessageModal && hasPatient && (
        <MessageModal
          patientId={appointment.patient_id!}
          patientName={patientName}
          clinicId={clinicId}
          onClose={() => setShowMessageModal(false)}
        />
      )}
    </>
  );
}
